import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import WaitlistDialog from './WaitlistDialog';
import { EARLY_ACCESS } from '../config';
import './Landing.css';

const TILES = [
  '🀇','🀈','🀉','🀊','🀋','🀌','🀍','🀎','🀏',
  '🀙','🀚','🀛','🀜','🀝','🀞','🀟','🀠','🀡',
  '🀀','🀁','🀂','🀃','🀄','🀅','🀆',
];

function TileGrid() {
  return (
    <div className="tile-grid" aria-hidden="true">
      {Array.from({ length: 48 }).map((_, i) => (
        <span
          key={i}
          className="tile-bg"
          style={{
            '--delay': `${(i * 0.37) % 6}s`,
            '--rot': `${(i * 19) % 34 - 17}deg`,
          }}
        >
          {TILES[i % TILES.length]}
        </span>
      ))}
    </div>
  );
}

export default function Landing() {
  const scrollToAbout = () =>
    document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' });

  // Which call to action opened it, so we can see which one actually converts.
  const [waitlist, setWaitlist] = useState(null);

  /* While the beta is closed these are buttons, not links: there is no account
   * to create yet. With EARLY_ACCESS off they go to the real sign-up. */
  const signUpCta = (source, className, children) => (
    EARLY_ACCESS
      ? <button className={className} onClick={() => setWaitlist(source)}>{children}</button>
      : <Link to="/login" className={className}>{children}</Link>
  );

  return (
    <div className="landing">
      <nav className="nav">
        <span className="nav-logo">🀄 Triad-Ha</span>
        <Link to="/login" className="btn btn-ghost">Log In</Link>
      </nav>

      <section className="hero">
        <TileGrid />
        <div className="hero-content">
          <p className="eyebrow">East Wind is Calling</p>
          <h1>
            Where Dragons Run<br />
            <span className="gold">and Pots Get Stacked</span>
          </h1>
          <p className="subline">
            The sharpest Mahjong meets the deepest PLO. Self-draw or double-suited,
            position is everything. Step to the felt — the tiles don't lie.
          </p>
          <div className="cta-group">
            {signUpCta('landing-hero', 'btn btn-primary', 'Deal Me In')}
            <button className="btn btn-secondary" onClick={scrollToAbout}>
              What is Triad-Ha?
            </button>
          </div>
        </div>
        <div className="scroll-hint" aria-hidden="true">↓</div>
      </section>

      <section className="about" id="about">
        <div className="about-inner">
          <p className="section-label">The Game</p>
          <h2>Pung. Pot it. Win.</h2>
          <p>
            Triad-Ha is built for players who move fast and think deeper.
            Whether you're reading blockers on a PLO rundown or counting out
            a self-drawn Mahjong, the edge compounds. One platform that
            respects both games — and the players sharp enough to play them.
          </p>
        </div>
      </section>

      <section className="features">
        <div className="feature">
          <span className="feature-icon">🀄</span>
          <h3>Riichi Ready</h3>
          <p>
            Lobby to live in seconds. East Wind, South Wind, West Wind —
            the table's always hot and a Pung waits around every corner.
          </p>
        </div>
        <div className="feature">
          <span className="feature-icon">🃏</span>
          <h3>Double-Suited Action</h3>
          <p>
            Freerolls, PLO cash games, and Mahjong tournaments under one roof.
            Four cards, four winds — double the equity, double the edge.
          </p>
        </div>
        <div className="feature">
          <span className="feature-icon">🀅</span>
          <h3>Stack the Kong</h3>
          <p>
            Chase the Kong. Call the pot. From micro-stakes to feature events,
            build your bankroll one tile — one hand — at a time.
          </p>
        </div>
      </section>

      <section className="signup-band">
        <h2>Ready to Ron?</h2>
        <p>Join the waitlist. Be first to the table when the gates open.</p>
        {/* The copy above always said waitlist; until this release the button
            under it went to a login page. Now it does what the sentence says. */}
        {signUpCta('landing-band', 'btn btn-primary btn-large',
          EARLY_ACCESS ? 'Join the Waitlist' : 'Create Your Account')}
      </section>

      <WaitlistDialog
        open={waitlist !== null}
        source={waitlist ?? 'unknown'}
        onClose={() => setWaitlist(null)}
      />

      <footer className="footer">
        <span className="nav-logo">🀄 Triad-Ha</span>
        <p className="footer-copy">© 2026 Triad-Ha. All rights reserved.</p>
      </footer>
    </div>
  );
}
